// Journal d'activité — de la clé brute à la phrase lisible.
//
// L'audit écrit des payloads riches (diffs from→to, noms, montants,
// dates) et personne ne les voyait : la page Activité rendait
// « acteur — libellé · targetType », avec une table de libellés qui
// couvrait 12 actions sur plus de cent — le reste s'affichait en clé
// brute (`leave.request_approve`). Le fil du tableau de bord était pire :
// l'action brute, sans libellé du tout.
//
// Deux morceaux ici :
//   ACTIVITY_ACTION_LABELS — un libellé français pour CHAQUE action émise
//     (greper `logActivity(` côté serveur pour la liste de référence).
//   activityLabel()        — le libellé, enrichi de ce que le payload
//     porte : sujet, période, from→to. Une action inconnue retombe sur
//     sa clé plutôt que sur du vide.
//
// Partagé client/serveur : la page Activité, le fil du tableau de bord
// et la colonne « détail » de l'export CSV disent tous la même chose.
import { GM_KIND_MENUS } from './grille.js';
// ─── libellés ────────────────────────────────────────────────
export const ACTIVITY_ACTION_LABELS = {
    // Saisie quotidienne
    'attendance.update': 'Pointage corrigé',
    'attendance.bulk': 'Pointages modifiés en lot',
    'attendance.decision': 'Décision modifiée',
    'attendance.notified': '« Prévenu ? » renseigné',
    'attendance.validate_l1': 'Saisie validée',
    'attendance.validate': 'Journée validée',
    'attendance.reopen': 'Journée rouverte',
    'attendance.reimport': 'Ré-import pointeuse (écrasement)',
    'pointeuse.import': 'Import pointeuse',
    // Grille mensuelle
    'monthly.submit': 'Grille mensuelle — étape',
    'monthly.paie_decision': 'Décision paie (grille)',
    'monthly.paie_adjustment': 'Heures ajustées (grille)',
    // Congés & récup
    'leave.transaction': 'Écriture au journal des congés',
    'leave.consume': 'Congé saisi',
    'leave.regularize': 'Régularisation de soldes',
    'leave.accrual': 'Acquisition mensuelle de congés',
    'leave.import_stock': 'Import de soldes initiaux',
    'leave.request_create': 'Demande de congé créée',
    'leave.request_approve': 'Demande de congé approuvée',
    'leave.request_reject': 'Demande de congé refusée',
    'leave.request_cancel': 'Demande de congé annulée',
    'leave.request_debit': 'Demande de congé débitée',
    'recup.claim_create': 'Récup — journée déclarée',
    'recup.claim_approve': 'Récup — journée validée',
    'recup.claim_reject': 'Récup — journée refusée',
    'recup.claim_cancel': 'Récup — déclaration annulée',
    // Employés
    'employee.create': 'Fiche employé créée',
    'employee.update': 'Fiche employé modifiée',
    'employee.delete': 'Employé archivé',
    'employee.balance_adjust': 'Solde ajusté',
    'employee.poste_add': 'Poste affecté',
    'employee.manager_set': 'Responsable hiérarchique défini',
    // Contrats
    'contract.decision': 'Décision de contrat',
    'contract.milestone': 'Jalon de contrat',
    // Paie
    'payroll.bootstrap': 'Mois de paie initialisé', // script de reprise
    'payroll.salary_override': 'Salaire du mois ajusté',
    'payroll.bonus_set': 'Bonus saisi',
    'payroll.note_update': 'Note de paie modifiée',
    'payroll.line_status': 'Statut de ligne de paie',
    'payroll.payment_method': 'Mode de paiement',
    'payroll.informed_toggle': 'Employé informé (paie)',
    'payroll.submit': 'Paie soumise pour validation',
    'payroll.validate': 'Paie validée',
    'payroll.send_back': 'Paie renvoyée en saisie',
    'payroll.reopen': 'Paie rouverte',
    'payroll.mark_sent': 'Virements marqués envoyés',
    'payroll.bank_export': 'Export bancaire généré',
    // Évaluations & objectifs
    'evaluation.mgr_submit': 'Évaluation soumise par le manager',
    'evaluation.auto_submit': 'Auto-évaluation soumise',
    'evaluation.oto_propose': 'Entretien proposé',
    'evaluation.oto_choose': 'Créneau d’entretien choisi',
    'evaluation.close': 'Évaluation clôturée',
    'evaluation.sign': 'Évaluation signée',
    'evaluation.remind': 'Relance d’évaluation',
    'objective.create': 'Objectif créé',
    'objective.propose': 'Objectif proposé',
    'objective.check': 'Point d’étape sur objectif',
    'objective.refuse': 'Objectif refusé',
    // Documents (Espace employé)
    'document.request_create': 'Document demandé',
    'document.request_update': 'Demande de document mise à jour',
    'document.request_cancel': 'Demande de document annulée',
    // Référentiels & configuration
    'holiday.create': 'Jour férié ajouté',
    'holiday.update': 'Jour férié modifié',
    'holiday.delete': 'Jour férié supprimé',
    'poste.create': 'Poste créé',
    'poste.update': 'Poste modifié',
    'poste.delete': 'Poste supprimé',
    'decisionType.create': 'Type de décision créé',
    'decisionType.update': 'Type de décision renommé',
    'decisionType.delete': 'Type de décision retiré',
    'settings.update': 'Paramètres modifiés',
    'entity.company.update': 'Identité société modifiée',
    'entity.logo.update': 'Logo de société mis à jour',
    'entity.logo.delete': 'Logo de société retiré',
    // Comptes & sécurité
    'user.invite': 'Utilisateur invité',
    'user.update': 'Accès utilisateur modifiés',
    'auth.login': 'Connexion',
    'auth.logout': 'Déconnexion',
    'auth.impersonate.start': 'Début de « Voir comme »',
    'auth.impersonate.stop': 'Fin de « Voir comme »',
    // Sorties
    'reports.pdf.generated': 'PDF généré',
    'reports.zip.generated': 'Attestations générées en lot',
    'email.send': 'Email de relance envoyé',
    'email.send.failed': 'Email de relance en échec',
    'cron.job.completed': 'Tâche planifiée terminée',
    'cron.weeklySummary.sent': 'Récap hebdo envoyé à la direction',
    'cron.weeklySummary.pending': 'Récap hebdo en attente de validation',
    'cron.weeklySummary.failed': 'Récap hebdo non envoyé — direction prévenue',
    // Historique — écrites par des scripts d'import ponctuels (reprise de
    // données 2026). Plus personne ne les émet, mais les lignes restent
    // au journal et doivent se lire comme les autres.
    'employee.hr_import': 'Import RH (fiches)',
    'employee.tracking_import': 'Import du fichier de suivi',
    'employee.salary_change': 'Salaire de référence modifié',
    'employee.probation_rule': 'Règle de période d’essai appliquée',
    // Recrutement
    'rec.requisition.create': 'Demande de recrutement créée',
    'rec.requisition.decision': 'Demande de recrutement arbitrée',
    'rec.requisition.resubmit': 'Demande de recrutement re-soumise',
    'rec.requisition.status': 'Demande de recrutement — statut',
    'rec.requisition.hiring_manager': 'Manager recruteur désigné',
    'rec.candidate.file_upload': 'Pièce du candidat ajoutée',
    'rec.candidate.file_delete': 'Pièce du candidat supprimée',
    'rec.candidate.create': 'Candidature créée',
    'rec.application.stage': 'Candidature — étape',
    'rec.application.reject': 'Candidature rejetée',
    'rec.application.vivier': 'Candidature placée au vivier',
    'rec.application.restore': 'Candidature repêchée du vivier',
    'rec.application.note': 'Note interne sur candidature',
    'rec.call.plan': 'Appel de préqualification planifié',
    'rec.call.close': 'Appel de préqualification clôturé',
    'rec.call.report': 'Appel de préqualification signalé terminé',
    'rec.call.unreport': 'Signalement « appel terminé » annulé',
    'rec.interview.create': 'Entretien planifié',
    'rec.interview.patch': 'Entretien modifié',
    'rec.scorecard.submit': 'Scorecard remplie',
    'rec.test.send': 'Cas pratique envoyé',
    'rec.test.submit': 'Cas pratique rendu',
    'rec.test.pay': 'Indemnité du cas pratique payée',
    'rec.test.score': 'Cas pratique noté',
    'rec.verification.reference': 'Référence appelée',
    'rec.verification.integrity': 'Vérification — intégrité renseignée',
    'rec.offer.bar_override': 'Dérogation à la barre d’embauche',
    'rec.application.neg_reply_dismiss': 'Réponse négative écartée',
    'rec.probation.checkpoint': 'Période d’essai — point de contrôle',
    'rec.probation.outcome': 'Période d’essai — objectif mis à jour',
    'rec.email.send': 'Email candidat envoyé',
    'rec.email.send.failed': 'Email candidat en échec',
    'rec.offer.create': 'Offre construite',
    'rec.offer.submit': 'Offre soumise à validation',
    'rec.offer.decision': 'Offre arbitrée',
    'rec.offer.send': 'Offre envoyée',
    'rec.offer.send.failed': 'Envoi d’offre en échec',
    'rec.offer.remind.failed': 'Relance d’offre en échec',
    'rec.offer.remind': 'Relance d’offre',
    'rec.offer.response': 'Réponse à l’offre',
    'rec.offer.patch': 'Brouillon d’offre ajusté',
    'rec.offer.pdf': 'PDF d’offre téléchargé',
    'rec.onboarding.create': 'Onboarding créé',
    'rec.onboarding.task': 'Tâche d’onboarding',
    'rec.onboarding.doc': 'Dossier administratif',
    'rec.onboarding.resource': 'Ressource d’onboarding partagée',
    'rec.onboarding.buddy': 'Buddy d’onboarding',
    'rec.onboarding.handoff': 'Onboarding — fiche employé créée',
    'rec.role.create': 'Rôle ajouté au catalogue',
    'rec.role.patch': 'Rôle du catalogue modifié',
    'rec.role.template': 'Template de rôle mis à jour',
    // Bureau (office manager)
    'bureau.check': 'Point de contrôle coché (Bureau)',
    'bureau.uncheck': 'Point de contrôle décoché (Bureau)',
    'bureau.report': 'Manquement signalé (Bureau)',
    'bureau.follow_create': 'Suivi créé (Bureau)',
    'bureau.follow_update': 'Suivi modifié (Bureau)',
    'bureau.follow_cancel': 'Suivi annulé (Bureau)',
    'bureau.step_done': 'Étape de suivi cochée (Bureau)',
    'bureau.step_undone': 'Étape de suivi décochée (Bureau)',
    'bureau.nudge': 'Relance envoyée (Bureau)',
    'bureau.stock_level': 'Niveau de stock modifié (Bureau)',
    'bureau.purchase_list': 'Liste d’achats préparée (Bureau)',
    'bureau.config_people': 'Responsable et référents du Bureau modifiés',
    'bureau.config_moment': 'Moment du Bureau réglé',
    'bureau.config_item': 'Point de contrôle du Bureau réglé',
    'bureau.config_stock': 'Article de stock du Bureau réglé',
    'bureau.config_bill': 'Facture récurrente du Bureau réglée',
};
const asObj = (v) => (v && typeof v === 'object' ? v : {});
const str = (v) => v == null || v === '' ? null : typeof v === 'object' ? null : String(v);
/** Le nombre porté par la valeur, ou null — jamais « [object Object] ». */
const num = (v) => typeof v === 'number' && Number.isFinite(v) ? v : null;
/** YYYY-MM-DD → JJ/MM. Toute autre valeur repart telle quelle. */
export function shortDate(v) {
    const s = str(v);
    if (!s)
        return null;
    const m = /^(\d{4})-(\d{2})-(\d{2})/.exec(s);
    return m ? `${m[3]}/${m[2]}` : s;
}
const MONTHS_FR = [
    'janvier', 'février', 'mars', 'avril', 'mai', 'juin',
    'juillet', 'août', 'septembre', 'octobre', 'novembre', 'décembre',
];
function monthLabel(p) {
    const y = Number(p.year);
    const m = Number(p.month);
    if (!Number.isFinite(y) || !Number.isFinite(m) || m < 1 || m > 12)
        return null;
    return `${MONTHS_FR[m - 1]} ${y}`;
}
// Décisions et étapes stockées en clé technique — rendues en clair.
const DECISION_FR = {
    PRESENT: 'Présent', TT: 'Télétravail', CONGE: 'Congé', MALADIE: 'Maladie',
    RDV_PERSO: 'RDV perso', RECUP: 'Récup', AUTRE: 'Autre',
    YES: 'oui', NO: 'non',
    // Statuts d'AttendanceRecord (le from→to d'un pointage corrigé).
    AUTO: 'pointeuse', CONGE_HALF: '½ congé', SICK: 'maladie',
    SICK_PAID: 'congé maladie', REMOTE: 'télétravail', ABSENT: 'absent',
    HOLIDAY: 'férié',
    // Réponse d'un candidat à une offre.
    acceptee: 'acceptée', refusee: 'refusée', contre: 'contre-proposition',
    DRAFT: 'brouillon', READY: 'prêt', VALIDATED_L1: 'saisie validée', VALIDATED: 'validée',
    PREPARED_BY_LAMIS: 'préparée', REVIEWED_BY_BOUCHRA: 'revue', VALIDATED_BY_CHOUAIB: 'validée',
};
const clear = (v) => {
    const s = str(v);
    return s == null ? 'aucune' : DECISION_FR[s] ?? s;
};
/** « X → Y » quand les deux côtés existent et diffèrent. */
function transition(from, to) {
    const a = str(from);
    const b = str(to);
    if (a == null && b == null)
        return null;
    if (a === b)
        return null;
    return `${clear(a)} → ${clear(b)}`;
}
// Les diffs sont écrits avec les noms de colonnes; « ttDays » ou
// « permsRevoked » ne veulent rien dire pour qui lit le journal.
const FIELD_FR = {
    name: 'nom', code: 'matricule', email: 'email', phone: 'téléphone',
    department: 'fonction', entityId: 'entité', managerId: 'responsable',
    salary: 'salaire', active: 'actif', hiredAt: 'date d’entrée',
    dateSortie: 'date de sortie', notes: 'notes', ttDays: 'jours de télétravail', payActualHours: 'heures réelles en paie',
    statsExcluded: 'exclu des statistiques', pointageExcluded: 'hors pointage', payrollExcluded: 'hors paie', paymentMethod: 'mode de paiement',
    congeBalance: 'solde de congés',
    recupBalance: 'solde de récup', contractType: 'type de contrat',
    cddEnd: 'fin de CDD', cddMotif: 'motif du CDD', probationStart: 'début d’essai',
    probationMonths: 'durée d’essai', probationRenewMonths: 'renouvellement d’essai',
    birthDate: 'date de naissance', cnas: 'n° CNAS', bankAccount: 'compte bancaire',
    emergencyName: 'contact d’urgence', emergencyPhone: 'téléphone d’urgence',
    emergencyRelation: 'lien du contact d’urgence',
    role: 'rôle', entityScope: 'portée', permsGranted: 'permissions accordées',
    permsRevoked: 'permissions retirées', modules: 'modules',
};
const fieldFr = (k) => FIELD_FR[k] ?? k;
/** Liste des champs touchés, depuis un objet `changes` {champ:{from,to}}. */
function changedFields(v) {
    const c = asObj(v);
    const keys = Object.keys(c);
    if (keys.length === 0)
        return null;
    return keys.length <= 4 ? keys.map(fieldFr).join(', ') : `${keys.length} champs`;
}
// Renommages: le détail dit déjà « ancien → nouveau », le libellé n'a
// pas à revenir une troisième fois en queue de ligne.
const LABEL_IN_DETAIL = new Set(['poste.update', 'decisionType.update', 'monthly.paie_decision']);
/** Qui/quoi la ligne concerne, avec le nom résolu serveur en priorité. */
function subjectOf(e, p) {
    return (str(e.subjectName) ??
        str(p.employeeName) ??
        str(p.name) ??
        str(p.candidate) ??
        (LABEL_IN_DETAIL.has(e.action) ? null : str(p.label)) ??
        // pas `p.role`: la branche recrutement le pousse déjà en tête, il
        // reviendrait une seconde fois en queue de ligne.
        str(p.email) ??
        str(p.employeeCode) ??
        null);
}
// ─── détail par famille ──────────────────────────────────────
function detailFor(e, p, opts) {
    const bits = [];
    const push = (v) => {
        if (v)
            bits.push(v);
    };
    const a = e.action;
    // Saisie quotidienne — la famille où le from→to compte le plus.
    if (a === 'attendance.decision') {
        push(transition(asObj(p.from).decisionType, asObj(p.to).decisionType));
    }
    else if (a === 'attendance.notified') {
        push(transition(asObj(p.from).notifiedInAdvance, asObj(p.to).notifiedInAdvance));
    }
    else if (a === 'attendance.update') {
        const f = asObj(p.from);
        const t = asObj(p.to);
        push(transition(f.checkIn, t.checkIn) && `entrée ${transition(f.checkIn, t.checkIn)}`);
        push(transition(f.checkOut, t.checkOut) && `sortie ${transition(f.checkOut, t.checkOut)}`);
        push(transition(f.status, t.status) && `statut ${transition(f.status, t.status)}`);
    }
    else if (a === 'attendance.validate_l1') {
        push(p.prepared != null ? `${p.prepared} ligne(s)` : null);
        push(p.arbitrated ? `${p.arbitrated} exception(s) arbitrée(s)` : null);
    }
    else if (a === 'attendance.validate') {
        push(p.validated != null ? `${p.validated} ligne(s)` : null);
    }
    else if (a === 'attendance.reopen') {
        push(p.reopened != null ? `${p.reopened} ligne(s)` : null);
        push(p.reversedLeaveDebits ? `${p.reversedLeaveDebits} débit(s) contre-passé(s)` : null);
        push(str(p.reason));
    }
    else if (a === 'attendance.bulk') {
        push(p.applied != null ? `${p.applied} ligne(s)` : null);
    }
    else if (a === 'pointeuse.import' || a === 'attendance.reimport') {
        push(p.inserted != null ? `${p.inserted} créés` : null);
        push(p.updatedTimes ? `${p.updatedTimes} mis à jour` : null);
        push(p.overlapRows ? `${p.overlapRows} écrasés` : null);
        push(Array.isArray(p.clearedDecisions) && p.clearedDecisions.length
            ? `${p.clearedDecisions.length} arbitrage(s) annulé(s)`
            : null);
        push(p.skippedValidated ? `${p.skippedValidated} refusés (jours validés)` : null);
    }
    // Grille mensuelle
    else if (a === 'monthly.submit') {
        push(transition(p.from, p.to));
        push(monthLabel(p));
    }
    else if (a === 'monthly.paie_decision') {
        // « J » seul ne dit rien: la même touche vaut « Sortie justifiée »
        // sur une journée courte et « Absence justifiée » sur une absence.
        const gm = (v) => {
            const key = str(v);
            const kind = str(p.kind);
            if (!key || !kind)
                return key;
            const menu = GM_KIND_MENUS[kind];
            return menu?.options.find((o) => o.key === key)?.label ?? str(p.label) ?? key;
        };
        push(transition(gm(p.from), gm(p.to)) ?? (str(p.label) && `→ ${p.label}`));
        push(str(p.kind) && `cellule « ${p.kind} »`);
    }
    else if (a === 'monthly.paie_adjustment') {
        push(p.manualHours != null ? `${p.manualHours} h` : null);
    }
    // Congés & récup
    else if (a === 'leave.consume') {
        push(p.days != null ? `${p.days} j` : null);
        push(str(p.type));
        push(p.from && p.to ? `${shortDate(p.from)} → ${shortDate(p.to)}` : null);
    }
    else if (a === 'leave.transaction' || a === 'employee.balance_adjust') {
        push(p.amount != null ? `${Number(p.amount) > 0 ? '+' : ''}${p.amount} j` : null);
        push(str(p.type));
        push(str(p.reason));
    }
    else if (a === 'leave.regularize') {
        push(p.applied != null ? `${p.applied} appliquée(s)` : null);
        push(p.skipped ? `${p.skipped} ignorée(s)` : null);
    }
    else if (a.startsWith('leave.request_') || a.startsWith('recup.claim_')) {
        push(p.days != null ? `${p.days} j` : null);
        push(str(p.type));
        push(p.startDate ? `${shortDate(p.startDate)}${p.endDate && p.endDate !== p.startDate ? ` → ${shortDate(p.endDate)}` : ''}` : null);
        push(str(p.motif));
    }
    // Employés & contrats
    else if (a === 'employee.create') {
        push(str(p.employeeCode));
    }
    else if (a === 'employee.update' || a === 'user.update' || a === 'settings.update') {
        push(changedFields(p.changes));
    }
    else if (a === 'employee.manager_set') {
        push(str(p.managerName) ? `→ ${p.managerName}` : 'responsable retiré');
    }
    else if (a === 'employee.delete') {
        push(shortDate(p.dateSortie) && `sortie ${shortDate(p.dateSortie)}`);
    }
    else if (a === 'contract.decision' || a === 'contract.milestone') {
        push(str(p.decision) ?? str(p.action));
    }
    // Paie
    else if (a.startsWith('payroll.')) {
        push(monthLabel(p));
        push(num(p.override) != null ? `${p.override} DA` : null);
        push(num(p.amount) != null ? `${p.amount} DA` : null);
        // `rows` est un compteur ici et un tableau de lignes dans les
        // écritures du script de reprise: n'interpoler qu'un nombre.
        push(num(p.rows) != null ? `${p.rows} ligne(s)` : Array.isArray(p.rows) ? `${p.rows.length} ligne(s)` : null);
        push(transition(p.from, p.to));
    }
    // Référentiels
    else if (a.startsWith('holiday.')) {
        // La date part en queue de ligne (`when`) et le nom sert de sujet:
        // ici, seulement ce qui a bougé — et seul un `update` porte un
        // `from` (l'ajout et la suppression n'ont rien à comparer).
        if (a === 'holiday.update') {
            const f = asObj(p.from);
            push(transition(f.date, p.date) && `déplacé ${shortDate(f.date)} → ${shortDate(p.date)}`);
            push(transition(f.name, p.name));
        }
    }
    else if (a.startsWith('poste.')) {
        // Même forme que decisionType: `from` = ancien libellé au renommage.
        if (a === 'poste.update')
            push(transition(p.from, p.label));
    }
    else if (a.startsWith('decisionType.')) {
        push(str(p.code));
        // `from` n'existe qu'au renommage: le payload de création ne porte
        // que le nouveau libellé, et « aucune → X » y serait faux.
        if (a === 'decisionType.update')
            push(transition(p.from, p.label));
    }
    // Sorties
    else if (a === 'email.send' || a === 'email.send.failed') {
        push(str(p.recipient));
        push(Array.isArray(p.kinds) ? p.kinds.join(', ') : null);
        push(shortDate(p.triggerDate));
        push(str(p.error));
    }
    else if (a === 'reports.pdf.generated') {
        const PDF_FR = {
            monthly: 'grille mensuelle', employee: 'fiche employé', attestations: 'attestations',
        };
        push(str(p.kind) && (PDF_FR[String(p.kind)] ?? str(p.kind)));
        push(str(p.month));
    }
    else if (a === 'cron.job.completed') {
        push(str(p.job));
    }
    else if (a === 'cron.weeklySummary.sent') {
        push(str(p.weekStart) && `semaine du ${str(p.weekStart)}`);
        push(num(p.flags) != null ? `${num(p.flags)} signalement(s)` : null);
    }
    else if (a === 'cron.weeklySummary.failed') {
        push(str(p.weekStart) && `semaine du ${str(p.weekStart)}`);
        push(p.accessLost === true ? `accès Gmail de ${str(p.sender) ?? '?'} à reconnecter` : str(p.reason));
    }
    else if (a === 'cron.weeklySummary.pending') {
        push(str(p.weekStart) && `semaine du ${str(p.weekStart)}`);
        const days = Array.isArray(p.pending) ? p.pending.length : null;
        push(days != null ? `${days} jour(s) non validé(s)` : null);
    }
    // Recrutement — le fil du module lit les mêmes payloads.
    else if (a.startsWith('rec.')) {
        push(str(p.role));
        push(transition(p.from, p.to));
        if (a === 'rec.requisition.decision' || a === 'rec.offer.decision') {
            push(p.ok === false ? 'refusée' : p.ok === true ? 'validée' : null);
            push(str(p.step) && `étape ${p.step}`);
        }
        if (p.version != null)
            push(`v${p.version}`);
        if (a === 'rec.email.send' || a === 'rec.email.send.failed') {
            push(str(p.template) && `« ${opts.templateLabel?.(String(p.template)) ?? p.template} »`);
        }
        if (a === 'rec.role.template') {
            push(str(p.template) && `« ${opts.templateLabel?.(String(p.template)) ?? p.template} »`);
            push(p.cleared === true ? 'vidé' : null);
        }
        if (a === 'rec.offer.response')
            push(str(p.response) && clear(p.response));
        if (a === 'rec.application.reject')
            push(str(p.motif));
        if (a === 'rec.onboarding.task')
            push(str(p.task));
        if (a === 'rec.onboarding.doc')
            push(str(p.doc));
        if (a === 'rec.onboarding.resource')
            push(str(p.resource));
        if (a === 'rec.onboarding.buddy')
            push(str(p.buddy) ?? 'retiré');
        if (a === 'rec.onboarding.handoff')
            push(str(p.employeeCode));
    }
    // Rien de reconnu : un from→to générique vaut mieux que rien.
    else {
        push(transition(p.from, p.to));
        push(changedFields(p.changes));
    }
    return bits.filter(Boolean);
}
// ─── point d'entrée ──────────────────────────────────────────
/**
 * Une phrase lisible pour une ligne d'audit : le libellé de l'action,
 * ce qui a changé, sur qui, et quand — dans cet ordre. Les morceaux
 * absents disparaissent au lieu de laisser des séparateurs vides.
 */
export function activityLabel(e, opts = {}) {
    const p = asObj(e.payload);
    const head = ACTIVITY_ACTION_LABELS[e.action] ?? e.action;
    const parts = detailFor(e, p, opts);
    const subject = subjectOf(e, p);
    if (subject)
        parts.push(subject);
    // Une date de journée (saisie, congé, grille) situe la ligne ; la date
    // de l'action elle-même est déjà dans la colonne horaire.
    const when = e.action === 'leave.consume' ? null : shortDate(p.date);
    if (when)
        parts.push(when);
    const entity = str(e.entityName);
    if (entity)
        parts.push(entity);
    return parts.length > 0 ? `${head} · ${parts.join(' · ')}` : head;
}
//# sourceMappingURL=activityLabels.js.map